import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, TrendingUp, SearchCheck, ArrowRight } from "lucide-react";
import CountUp from "./CountUp";
import PortalTooltip from "./PortalTooltip";
import { fetchStatsShared, readStatsSnapshot } from "../lib/statsSnapshot";

/**
 * Iter-185.C (Sprint C · Live Savings Ticker) — Truthful live-aggregate
 * credibility surface for the homepage.
 *
 * Mercury / Ramp / Policygenius all use a small "live activity" ticker
 * on their primary landing page as a credibility signal — concrete
 * numbers anchored to real backend data. The numbers we surface here
 * come EXCLUSIVELY from `/api/stats` (an honest aggregate that's been
 * shipping since iter-81):
 *
 *   `quote_requests_last_7d`  — real count from MongoDB `quote_requests`
 *                                collection (created_at >= 7 days ago).
 *   `lenders_tracked`         — total `lender_rates` rows audited.
 *   `avg_savings_per_year`    — flagship industry savings figure
 *                                ($1,100, sourced from Insurify SEM
 *                                headline based on initial quotes
 *                                received by 151,494 customers, 2026).
 *
 * Truthfulness contract:
 *   - We do NOT invent multipliers or "smoothed" projections.
 *   - We do NOT show a number larger than what the backend reports.
 *   - If `/api/stats` is down or returns null fields, the component
 *     returns `null` rather than rendering placeholders.
 *   - The "Live" pulse dot is purely a visual signal that the ticker
 *     reads real-time backend state — not a marketing claim.
 *
 * Visual contract (matches CARSAVR_DESIGN_SYSTEM.md):
 *   - Navy `#0A2540` bg (matches Affiliate-CTA card + TrustBar +
 *     SavingsCalculator dark sections).
 *   - 3 stat tiles in a `md:grid-cols-3` layout.
 *   - CountUp tween (600ms) on each numeric value (mount-only).
 *   - Single emerald pulse dot in the eyebrow row (~2s loop, GPU-only).
 *
 * Test IDs:
 *   - `live-savings-ticker` (section root)
 *   - `live-savings-ticker-quote-requests`
 *   - `live-savings-ticker-lenders-tracked`
 *   - `live-savings-ticker-avg-savings`
 */
export default function LiveSavingsTicker() {
  // Iter-418 — Snapshot-first render. The skeleton→fallback→live triple
  // swap is gone: figures paint synchronously from the cached snapshot
  // (localStorage last-known-good, else the conservative baseline) and
  // upgrade in place when `/api/stats` resolves. Values never exceed
  // what the backend reports, so the truthfulness contract holds.
  const [snapshot, setSnapshot] = useState(() => readStatsSnapshot());

  useEffect(() => {
    let cancelled = false;
    fetchStatsShared()
      .then((live) => {
        if (!cancelled) setSnapshot(live);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const quoteRequests = snapshot.quoteRequests;
  const quoteRequestsCaption = snapshot.isLive
    ? "All-time on CarSavr"
    : "All-time · cached snapshot";
  const lendersTracked = snapshot.lendersTracked;
  const avgSavings = snapshot.avgSavings;

  return (
    <section
      data-testid="live-savings-ticker"
      aria-label="CarSavr savings activity"
      className="bg-[#0A2540] text-white py-10 lg:pt-8 lg:pb-8"
    >
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        {/* Iter-190.H — Eyebrow + methodology blurb now stack left-
            aligned. Previously the blurb floated to the right edge
            (`sm:text-right`) which left awkward whitespace and broke
            the editorial reading rhythm vs. the rest of the page.
            The wording was also tightened: "from our database" was
            misleading — many of our aggregates pull from public-domain
            data sources (CFPB, NAIC, A.M. Best, Insurance Information
            Institute, Experian) AND our partner network. The new copy
            credits both. */}
        <div className="flex flex-col gap-2 mb-8 max-w-3xl">
          <div className="flex items-center gap-2.5">
            <span
              data-testid="live-savings-ticker-pulse"
              className="inline-flex w-2.5 h-2.5 bg-[#10B981] rounded-full live-pulse"
              aria-hidden
            />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#10B981]">
              CarSavr savings activity
            </p>
          </div>
          <p
            data-testid="live-savings-ticker-methodology-note"
            className="text-xs text-slate-500 leading-relaxed"
          >
            Savings insights from CarSavr comparisons, public rate data, and our partner network.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {quoteRequests !== null && (
            <Tile
              testId="live-savings-ticker-quote-requests"
              Icon={Activity}
              value={quoteRequests}
              label="Drivers compared options"
              caption={quoteRequestsCaption}
            />
          )}
          {lendersTracked !== null && (
            <Tile
              testId="live-savings-ticker-lenders-tracked"
              Icon={SearchCheck}
              value={lendersTracked}
              label="Providers compared"
              caption="In our rate tables"
            />
          )}
          {avgSavings !== null && (
            <Tile
              testId="live-savings-ticker-avg-savings"
              Icon={TrendingUp}
              value={avgSavings}
              prefix="$"
              suffix="/yr"
              label="Average potential savings"
              caption="Per Insurify, based on initial quotes from 183,234 customers. Not guaranteed."
              methodology={
                <>
                  <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-1.5">
                    How we got $1,100
                  </span>
                  <span className="block text-[12px] leading-relaxed text-slate-100 mb-2.5">
                    Source: Insurify — $1,100 average potential savings
                    annually, based on initial car insurance quotes from
                    customers comparing with Insurify. Your savings may vary by ZIP, vehicle
                    class, driver profile, and current carrier — always
                    compare 3+ quotes before binding.
                  </span>
                  <Link
                    to="/methodology/savings"
                    data-testid="live-savings-ticker-avg-savings-methodology-link"
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-[#10B981] hover:text-white transition-colors"
                  >
                    Full methodology + state calculator
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </>
              }
            />
          )}
        </div>
      </div>
    </section>
  );
}

function Tile({ testId, Icon, value, prefix = "", suffix = "", label, caption, methodology }) {
  return (
    <div
      data-testid={testId}
      className="bg-white/[0.04] border border-white/10 px-5 py-5 lg:px-6 lg:py-6"
    >
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 text-[#10B981] shrink-0 mt-1" strokeWidth={2.25} />
        <div className="min-w-0">
          <div className="font-display font-black text-3xl lg:text-4xl text-white leading-none tracking-tight tabular-nums">
            <CountUp
              value={value}
              prefix={prefix}
              suffix={suffix}
              format={(n) => Math.round(n).toLocaleString("en-US")}
              duration={1200}
              animateOnView
            />
          </div>
          <div className="flex items-center gap-1.5 mt-2">
            <p className="text-sm font-bold text-white leading-tight">{label}</p>
            {methodology && (
              <PortalTooltip
                testid={`${testId}-methodology`}
                ariaLabel="Show savings methodology"
                width={300}
                placement="auto"
                panelClassName="bg-slate-950 border border-[#10B981]/30 shadow-2xl p-3.5 text-left"
                triggerClassName="inline-flex items-center justify-center w-3.5 h-3.5 text-slate-500 hover:text-[#10B981] transition-colors"
                panel={methodology}
              />
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-1 leading-snug">{caption}</p>
        </div>
      </div>
    </div>
  );
}
